import { SentryProvider } from '@reapit-sales/sentry-provider'
import { FC, ReactNode } from 'react'
import { MixpanelProvider } from '@reapit-sales/mixpanel-provider'
import { MIXPANEL_TRACE_SAMPLE_RATE, SENTRY_TRACE_SAMPLE_RATE } from './constants'

interface CRMTrackingProviderProps {
  children: ReactNode
}

/**
 * CRMTrackingProvider
 * This component wraps the CRM application with Sentry and Mixpanel tracking providers.
 * It initializes Sentry and Mixpanel with environment-specific configurations.
 * If the Sentry DSN or Mixpanel token is not provided,
 * it will log a warning and not initialize the respective provider.
 */
export const CRMTrackingProvider: FC<CRMTrackingProviderProps> = ({ children }) => {
  // Environment variables for Sentry and Mixpanel
  const environment = import.meta.env.MODE
  const sentryDsn = import.meta.env.VITE_CRM_SENTRY_DSN
  const release = `crm@${__REAPIT_SALES_APP_VERSION__}`
  const sentryTracesSampleRate = SENTRY_TRACE_SAMPLE_RATE
  const mixpanelToken = import.meta.env.VITE_MIXPANEL_TOKEN
  const mixpanelTracesSampleRate = MIXPANEL_TRACE_SAMPLE_RATE
  const initialUserId = 'anonymous' // Default initial user ID for Mixpanel
  const config = {} // Mixpanel configuration object, can be extended as needed

  // Sentry configuration for THIS CRM app
  const sentryConfig = {
    dsn: sentryDsn || '',
    environment: environment,
    release: release,
    tracesSampleRate: sentryTracesSampleRate,
  }

  // Mixpanel configuration for THIS CRM app
  const mixpanelConfig = {
    token: mixpanelToken || '',
    initialDistinctId: initialUserId,
    tracesSampleRate: mixpanelTracesSampleRate,
    config: config,
  }

  if (!sentryDsn) {
    console.warn('Sentry DSN is not configured. Sentry will not be initialized.')
  }
  if (!mixpanelToken) {
    console.warn('Mixpanel Token is not configured. Mixpanel will not be initialized.')
  }

  // Only render providers if DSN/Token are available
  return (
    <>
      {/* SentryProvider wraps MixpanelProvider if Sentry DSN is present */}
      {sentryDsn ? (
        <SentryProvider {...sentryConfig}>
          {/* MixpanelProvider wraps children if Mixpanel token is present */}
          {mixpanelToken ? (
            <MixpanelProvider {...mixpanelConfig}>{children}</MixpanelProvider>
          ) : (
            children // Render children directly if Mixpanel token is missing
          )}
        </SentryProvider>
      ) : mixpanelToken ? (
        // If Sentry DSN is missing, but Mixpanel token is present, just render MixpanelProvider
        <MixpanelProvider {...mixpanelConfig}>{children}</MixpanelProvider>
      ) : (
        // If both are missing, just render children
        children
      )}
    </>
  )
}
